// @file Handles Canvas format dragging
import { $ } from '@@/bits/jquery_ui_resizable'
import { getJsPlumbInstance } from '@@/bits/jsplumb'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import type { Post } from '@@/types'
import '@@/vendor/toe.min'
import { nextTick, ref } from 'vue'

export const useDraggablePost = (
  post: Post,
): {
  initializeDraggable: (_containerElement: HTMLElement) => Promise<void>
  destroyDraggable: () => void
} => {
  const containerElement = ref<HTMLElement>()

  const _savePosition = (position: { top: number; left: number }): void => {
    useSurfacePostsStore().movePost({
      postCid: post.cid,
      position: position,
    })
  }

  const _makeDraggable = (): void => {
    if (containerElement.value == null) return
    const rootElement = $(containerElement.value) as any

    rootElement.draggable({
      containment: [0, 0],
      cancel:
        '[contenteditable], .ui-resizable-handle, object, embed, input, textarea, button, select, option, .no-drag-canvas',
      cursor: 'move',
      snap: '.surface-post',
      snapTolerance: 4,
      start: (_event, _ui) => {
        if (containerElement.value == null) return

        rootElement.addClass('noclick is-moving')
        const boundingRect = containerElement.value.parentElement?.getBoundingClientRect()
        rootElement.draggable('option', 'containment', [boundingRect?.left || 0, boundingRect?.top || 0])
        rootElement.draggable('instance')._setContainment()
        useSurfacePostsDragAndDropStore().isAPostDragging = true
      },
      drag: (_event, _ui) => {
        if (containerElement.value == null) return

        getJsPlumbInstance()?.revalidate(containerElement.value)
      },
      stop: (_event, ui) => {
        setTimeout(() => {
          rootElement.removeClass('noclick is-moving')
        }, 100)
        _savePosition(ui.position)
        useSurfacePostsDragAndDropStore().isAPostDragging = false
      },
    })
  }

  const initializeDraggable = async (_containerElement: HTMLElement): Promise<void> => {
    containerElement.value = _containerElement
    await nextTick()
    _makeDraggable()
  }

  const destroyDraggable = (): void => {
    if (!containerElement.value) return
    const draggableInstance = $(containerElement.value).off('.draggable').draggable('instance')
    if (draggableInstance) (draggableInstance as any).destroy()
  }

  return {
    initializeDraggable,
    destroyDraggable,
  }
}
